import React, { useEffect } from "react";
import * as helpers from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { ReportSettingsModel } from "@app/exports/exportModels";
import CheckItem from "@app/shared/CheckItem";
import { QESButton } from "@app/shared/qes-standards/Button";

interface ReportSettingsSaveActionProps {
  currentReportSettings: ReportSettingsModel;
  isSelectionValid: boolean;
  reportSettingsHasChanges: boolean;
  reportSettingsHasInvalidChanges: boolean;
  reportSettingsList: ReportSettingsModel[];
  saveClicked: boolean;
  setCreateReportSettingsNameInputDisabled: (disabled: boolean) => void;
  setCurrentReportSettings: (rs: ReportSettingsModel) => void;
  setReportSettingsHasChanges: (hasChanges: boolean) => void;
  setReportSettingsList: (rs: ReportSettingsModel[]) => void;
  setSaveClicked: (clicked: boolean) => void;
  stringifyStateReportSettings: () => string;
}

export const ReportSettingsSaveAction: React.FC<React.PropsWithChildren<ReportSettingsSaveActionProps>> = (props: ReportSettingsSaveActionProps) => {
  const {
    currentReportSettings,
    isSelectionValid,
    reportSettingsHasChanges,
    reportSettingsHasInvalidChanges,
    reportSettingsList,
    saveClicked,
    setCreateReportSettingsNameInputDisabled,
    setCurrentReportSettings,
    setReportSettingsHasChanges,
    setReportSettingsList,
    setSaveClicked,
    stringifyStateReportSettings,
  } = props;

  useEffect(() => {
    /* tslint:disable:no-empty */
  }, [
    currentReportSettings,
    isSelectionValid,
    reportSettingsHasChanges,
    reportSettingsHasInvalidChanges,
    reportSettingsList,
    saveClicked
  ]);

  const updateReportSettingsList = (id: number, key: string, value: (string | boolean)) => {
    const updateValueByKey = <T, K extends keyof T>(obj: T, key: K, value: T[K]) => obj[key] = value;

    updateValueByKey<ReportSettingsModel, keyof ReportSettingsModel>(
      reportSettingsList[reportSettingsList.findIndex(rs => rs.id === id)],
      key as keyof ReportSettingsModel,
      value);
    setReportSettingsList(reportSettingsList);
  }

  const handleIsDefaultCheck = (isDefault: boolean) => {
    setSaveClicked(false);
    setCurrentReportSettings({ ...currentReportSettings, isDefault });
    setReportSettingsHasChanges(true);
    if (currentReportSettings.id)
      updateReportSettingsList(currentReportSettings.id, "isDefault", isDefault);
  }

  const handleSaveClick = async () => {
    setCreateReportSettingsNameInputDisabled(true);
    setSaveClicked(true);
    let saved = false;
    if (currentReportSettings.id) { // id value indicates it has been persisted and is an existing RS
      const reportSettingsJson = stringifyStateReportSettings();

      let oldDefaults: ReportSettingsModel[];
      if (currentReportSettings.isDefault) {
        oldDefaults = reportSettingsList.filter(rs => rs.id !== currentReportSettings.id && rs.isDefault);
        oldDefaults.forEach(rs => rs.isDefault = false); // ensures any other defaults are reset
      }
      const listToUpdate = [{ ...currentReportSettings, reportSettingsJson }, ...(oldDefaults ? oldDefaults : [])];
      saved = await helpers.saveUpdatedReportSettingsList(listToUpdate);
      if (saved) {
        const prevList = reportSettingsList
          .filter(rs => !listToUpdate
            .map(upd => upd.id)
            .includes(rs.id));
        setReportSettingsList([...prevList, ...listToUpdate]);
      }
    }
    else {
      const reportSettingsJson = stringifyStateReportSettings(); // make sure to capture any settings updates before saving
      const savedReportSettings = await helpers.saveNewReportSettings({ ...currentReportSettings, reportSettingsJson });
      saved = !!(savedReportSettings && savedReportSettings.id);
      if (savedReportSettings.isDefault)
        reportSettingsList.forEach(rs => rs.isDefault = false);

      setCurrentReportSettings(savedReportSettings);
      setReportSettingsList([...reportSettingsList, savedReportSettings]);
    }    
    setReportSettingsHasChanges(false);
  }

  return (
    <div className="row mt-4">
      <div className="col-12 col-sm-6">
        <div className="btn-group-vertical">
          <CheckItem
            name={"Set as default setting"}
            checked={currentReportSettings ? currentReportSettings.isDefault : false}
            indeterminate={false}
            disabled={!currentReportSettings || !isSelectionValid}

            onChecked={() => handleIsDefaultCheck(true)}
            onUnchecked={() => handleIsDefaultCheck(false)}
          />
          <QESButton
            qesTestId="report-settings-save-action_save-settings"
            disabled={!currentReportSettings || !reportSettingsHasChanges || !isSelectionValid || reportSettingsHasInvalidChanges}
            className="me-2"
            onClick={() => handleSaveClick()}>
            {(saveClicked && !reportSettingsHasChanges) ? "Settings Saved!" : "Save Settings"}
          </QESButton>
        </div>
      </div>
      <div className="col-12 col-sm-6">
      </div>
    </div>
  );
}